/** 학생 관리 — 엑셀형 표 (추가/수정/삭제 + 소속 수업 표시) */
import React, { useEffect, useState, useCallback } from 'react';
import { IoAddCircle, IoTrashOutline, IoCreateOutline, IoClose } from 'react-icons/io5';
import academyService from '../../services/academyService';
import { useConsoleAcademy } from '../../components/console/ConsoleLayout';
import {
  PageHead, PageTitle, PrimaryBtn, GhostBtn, Card, Table, Empty, IconBtn,
  Overlay, Modal, ModalHead, ModalBody, ModalFoot, Field,
} from './consoleUi';

const fmtPhone = (p) => {
  const d = String(p || '').replace(/[^0-9]/g, '');
  if (d.length === 11) return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  return p || '';
};
const fmtDate = (ts) => {
  if (!ts) return '–';
  const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
  if (Number.isNaN(d.getTime())) return '–';
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
};

const ConsoleStudentsPage = () => {
  const { academy } = useConsoleAcademy();
  const aid = academy.id;
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} (신규) | student (수정)
  const [form, setForm] = useState({ name: '', phone: '', age: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        academyService.getStudents(aid),
        academyService.getClasses(aid),
      ]);
      setStudents(s);
      setClasses(c);
    } finally {
      setLoading(false);
    }
  }, [aid]);

  useEffect(() => { load(); }, [load]);

  const classesOf = (sid) =>
    classes.filter((c) => (c.studentIds || []).includes(sid)).map((c) => c.name);

  const openNew = () => { setForm({ name: '', phone: '', age: '' }); setEditing({}); };
  const openEdit = (s) => {
    setForm({ name: s.name || '', phone: s.phone || '', age: s.age || '' });
    setEditing(s);
  };

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const data = { name: form.name.trim(), phone: form.phone.trim(), age: form.age ? Number(form.age) : '' };
      if (editing.id) {
        await academyService.updateStudent(aid, editing.id, data);
      } else {
        await academyService.addStudent(aid, data);
      }
      setEditing(null);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`'${s.name}' 학생을 삭제할까요?`)) return;
    await academyService.deleteStudent(aid, s.id);
    await load();
  };

  return (
    <>
      <PageHead>
        <PageTitle>
          <h2>학생 관리</h2>
          <p>전체 {students.length}명 · 이름·연락처·소속 수업을 한눈에 관리하세요.</p>
        </PageTitle>
        <PrimaryBtn onClick={openNew}><IoAddCircle size={18} /> 학생 추가</PrimaryBtn>
      </PageHead>

      <Card $pad="0">
        {loading ? (
          <Empty>불러오는 중…</Empty>
        ) : students.length === 0 ? (
          <Empty>등록된 학생이 없어요. ‘학생 추가’로 시작하세요.</Empty>
        ) : (
          <Table>
            <thead>
              <tr>
                <th style={{ width: 56 }}>#</th>
                <th>이름</th>
                <th>나이</th>
                <th>학부모 연락처</th>
                <th>소속 수업</th>
                <th>등록일</th>
                <th style={{ width: 96, textAlign: 'right' }}>관리</th>
              </tr>
            </thead>
            <tbody>
              {students.map((s, i) => {
                const cls = classesOf(s.id);
                return (
                  <tr key={s.id}>
                    <td style={{ color: '#9CA3AF' }}>{i + 1}</td>
                    <td style={{ fontWeight: 700, color: '#111827' }}>{s.name}</td>
                    <td>{s.age ? `${s.age}세` : '–'}</td>
                    <td>{fmtPhone(s.phone)}</td>
                    <td>{cls.length ? cls.join(', ') : <span style={{ color: '#C4C9D2' }}>미배정</span>}</td>
                    <td style={{ color: '#9CA3AF' }}>{fmtDate(s.createdAt)}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <IconBtn onClick={() => openEdit(s)} title="수정"><IoCreateOutline size={18} /></IconBtn>
                      <IconBtn $danger onClick={() => remove(s)} title="삭제"><IoTrashOutline size={18} /></IconBtn>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>

      {editing && (
        <Overlay onClick={() => !saving && setEditing(null)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHead>
              {editing.id ? '학생 수정' : '학생 추가'}
              <IconBtn onClick={() => setEditing(null)}><IoClose size={18} /></IconBtn>
            </ModalHead>
            <ModalBody>
              <Field>
                <span className="lab">이름</span>
                <input autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </Field>
              <Field>
                <span className="lab">나이</span>
                <input type="number" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} />
              </Field>
              <Field>
                <span className="lab">학부모 연락처</span>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </Field>
            </ModalBody>
            <ModalFoot>
              <GhostBtn onClick={() => setEditing(null)}>취소</GhostBtn>
              <PrimaryBtn onClick={save} disabled={saving || !form.name.trim()}>
                {saving ? '저장 중…' : '저장'}
              </PrimaryBtn>
            </ModalFoot>
          </Modal>
        </Overlay>
      )}
    </>
  );
};

export default ConsoleStudentsPage;
